import { useMutation } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, useWatch } from "react-hook-form";
import type { Currency } from "../entities/ledger/types";
import { ensureWorkspace } from "../features/workspace/api";
import { setupSchema, type SetupForm } from "../features/workspace/model";
import { humanError } from "../shared/api/client";
import { ChipGroup } from "../shared/ui/ChipGroup";

interface SetupPageProps {
  onReady: (currency: Currency) => void;
  onNotify: (message: string) => void;
}

export function SetupPage({ onReady, onNotify }: SetupPageProps) {
  const form = useForm<SetupForm>({ resolver: zodResolver(setupSchema), defaultValues: { currency: "GEL", limit: "3000.00" } });
  const currency = useWatch({ control: form.control, name: "currency" });
  const mutation = useMutation({
    mutationFn: ({ currency: selectedCurrency, limit }: SetupForm) => ensureWorkspace(selectedCurrency, limit, true),
    onSuccess: (_ids, values) => {
      onNotify("Your Ledgerly workspace is ready.");
      onReady(values.currency as Currency);
    },
  });

  return (
    <section className="onboarding view" aria-labelledby="onboarding-title">
      <div className="section-heading">
        <p className="eyebrow">First setup</p>
        <h1 id="onboarding-title">Define the guardrail.</h1>
        <p>Ledgerly starts with the one thing that matters: how much you can spend this month.</p>
      </div>
      <form className="setup-card" onSubmit={form.handleSubmit((values) => mutation.mutate(values))}>
        <div className="field-grid">
          <div className="field">
            <span className="field-label">Base currency</span>
            <ChipGroup
              label="Base currency" value={form.watch("currency")}
              onChange={(value) => form.setValue("currency", value as "GEL" | "USD" | "EUR" | "GBP", { shouldValidate: true })}
              options={[
                { value: "GEL", label: "GEL — Georgian lari" },
                { value: "USD", label: "USD — US dollar" },
                { value: "EUR", label: "EUR — Euro" },
                { value: "GBP", label: "GBP — Pound sterling" },
              ]}
            />
          </div>
          <label>
            Spending target this month
            <span className="amount-input">
              <input {...form.register("limit")} inputMode="decimal" required />
              <span>{currency}</span>
            </span>
          </label>
        </div>
        <p className="form-error" role="alert">{form.formState.errors.limit?.message ?? ""}</p>
        <div className="setup-explainer">
          <span>Ledgerly will create</span>
          <ul>
            <li>one financial book</li>
            <li>cash, expense and income accounts</li>
            <li>a spending tracker with this month's target</li>
          </ul>
        </div>
        <button className="primary-button" type="submit" disabled={mutation.isPending}>
          <span>{mutation.isPending ? "Creating…" : "Create workspace"}</span><span aria-hidden="true">→</span>
        </button>
        <p className="form-error" role="alert">{mutation.error ? humanError(mutation.error) : ""}</p>
      </form>
    </section>
  );
}
